import React from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Lightning } from 'phosphor-react';

import { IEmpresaOption } from '../../../../../../hooks/useContaCilImportar';
import {
  IContaCilRegra,
  IOutrosRankingItem,
  useContaCilOutrosRanking,
} from '../../../../../../hooks/useContaCilRegras';
import { C, fmtValor, trimKeywordNoise } from '../regrasHelpers';

interface OutrosRankingTabProps {
  empresa: IEmpresaOption | null;
  banco: string | null;
  onCriarRegra: (prefill: Partial<IContaCilRegra>) => void;
}

export function OutrosRankingTab({ empresa, banco, onCriarRegra }: OutrosRankingTabProps) {
  const { data = [], isFetching } = useContaCilOutrosRanking({
    empresaId: empresa?.value,
    banco,
    limit: 50,
  });

  if (!empresa) {
    return (
      <Box bg="rgba(255,255,255,0.65)" borderRadius="3xl" p={10} textAlign="center">
        <Text fontSize="sm" color={C.muted}>
          Selecione uma empresa para caçar os padrões em &quot;Outros&quot;.
        </Text>
      </Box>
    );
  }

  const maxQtd = data.length ? Math.max(...data.map(d => d.qtd)) : 1;

  const criar = (item: IOutrosRankingItem) => {
    const kwBase = (item.sugestao_kw || trimKeywordNoise(item.exemplo)).trim();
    onCriarRegra({
      kw: kwBase ? [kwBase] : [],
      dc: item.dc,
      banco: banco || null,
    });
  };

  return (
    <Box
      bg="rgba(255,255,255,0.65)"
      backdropFilter="blur(28px) saturate(200%)"
      sx={{ WebkitBackdropFilter: 'blur(28px) saturate(200%)' }}
      borderWidth="1px"
      borderColor="rgba(255,255,255,0.6)"
      borderRadius="3xl"
      overflow="hidden"
    >
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor={C.hairline}
      >
        <Text fontSize="sm" fontWeight="600" color={C.texto}>
          Padrões frequentes em &quot;Outros&quot;
          {isFetching && <Spinner size="xs" ml={2} color={C.brand} thickness="2px" />}
        </Text>
        <Text fontSize="xs" color={C.muted}>
          {data.length} padrões
        </Text>
      </Flex>

      {data.length === 0 && !isFetching ? (
        <Box p={10} textAlign="center">
          <Text fontSize="sm" color={C.verdeText} fontWeight="600">
            Nenhum gap! Todo padrão frequente já tem regra.
          </Text>
        </Box>
      ) : (
        data.map((item, idx) => (
          <Flex
            key={`${item.amostra}-${item.dc}`}
            align="center"
            gap={3}
            px={4}
            py={4}
            borderBottomWidth={idx < data.length - 1 ? '1px' : '0'}
            borderColor="rgba(15,23,42,0.05)"
            _hover={{ bg: C.rowHover }}
            transition="background 0.18s ease"
          >
            <Text fontSize="11px" fontWeight="700" color={C.subtle} minW="28px">
              #{idx + 1}
            </Text>
            <Box flex={1} minW={0}>
              <Flex align="center" gap={2} mb={1}>
                <Box
                  as="span"
                  w="18px"
                  h="18px"
                  borderRadius="md"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="10px"
                  fontWeight="700"
                  bg={item.dc === 'C' ? C.vermelhoTint : C.verdeTint}
                  color={item.dc === 'C' ? C.vermelhoText : C.verdeText}
                  flexShrink={0}
                >
                  {item.dc}
                </Box>
                <Text fontSize="sm" fontWeight="600" color={C.texto} isTruncated>
                  {item.exemplo}
                </Text>
              </Flex>
              <Box h="8px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.round((item.qtd / maxQtd) * 100)}%` }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  style={{
                    height: '100%',
                    background: `linear-gradient(to right, ${C.ambar}, ${C.rosa})`,
                  }}
                />
              </Box>
            </Box>
            <Box textAlign="right" minW="70px">
              <Text fontSize="md" fontWeight="700" color={C.texto}>
                {item.qtd}
                <Text as="span" color={C.subtle} fontSize="xs">
                  ×
                </Text>
              </Text>
              <Text fontSize="10px" color={C.subtle}>
                R$ {fmtValor(item.soma)}
              </Text>
            </Box>
            <Button
              size="sm"
              borderRadius="full"
              px={4}
              color="white"
              fontWeight="600"
              bgGradient="linear(135deg, #F59E0B, #EC4899)"
              boxShadow="0 6px 16px -4px rgba(236,72,153,0.40)"
              _hover={{ filter: 'brightness(1.05)', transform: 'translateY(-1px)' }}
              _active={{ transform: 'translateY(0)' }}
              leftIcon={<Lightning size={14} weight="fill" />}
              onClick={() => criar(item)}
            >
              Criar regra
            </Button>
          </Flex>
        ))
      )}
    </Box>
  );
}
